/**
 * Animation Store
 * Manages timeline state and animation playback
 * Uses the new timeline-driven system with Web Animations API
 */

import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import type {
  Timeline,
  AnimationTrack,
  Keyframe,
  PlaybackState,
  AnimationPreset,
} from '@/types/animation';
import { createTimeline, createTrack, createKeyframe, calculateTimelineDuration } from '@/types/animation';
import { AnimationEngine } from '@/lib/animation/engine';
import { createTrackFromPreset } from '@/lib/animation/presets';

// Lazy import to avoid circular dependency
const recordChange = (label: string) => {
  import('./historyStore').then(({ useHistoryStore }) => {
    useHistoryStore.getState().recordChange(label);
  });
};

// ============================================================================
// TYPES
// ============================================================================

interface AnimationState {
  // Timeline data
  timeline: Timeline | null;

  // Playback state
  playbackState: PlaybackState;
  currentTime: number;
  duration: number;

  // Selection
  selectedTrackId: string | null;
  selectedKeyframeIndex: number | null;

  // UI state
  timelineZoom: number; // Timeline zoom level (pixels per second)
  timelineScrollOffset: number; // Timeline scroll position
  showTimeline: boolean; // Whether timeline panel is visible

  // Engine reference (not serialized)
  _engine: AnimationEngine | null;
}

interface AnimationActions {
  // Timeline management
  initTimeline: () => void;
  setTimeline: (timeline: Timeline) => void;
  clearTimeline: () => void;
  updateTimelineSettings: (settings: Partial<Pick<Timeline, 'loop' | 'playbackRate' | 'duration'>>) => void;

  // Track management
  addTrack: (layerId: string, name?: string) => AnimationTrack;
  addTrackFromPreset: (layerId: string, preset: AnimationPreset, options?: {
    startTime?: number;
    duration?: number;
  }) => AnimationTrack;
  updateTrack: (trackId: string, updates: Partial<AnimationTrack>) => void;
  deleteTrack: (trackId: string) => void;
  duplicateTrack: (trackId: string) => AnimationTrack | null;
  setTrackEnabled: (trackId: string, enabled: boolean) => void;

  // Keyframe management
  addKeyframe: (trackId: string, time: number, properties: Keyframe['properties']) => void;
  updateKeyframe: (trackId: string, keyframeIndex: number, updates: Partial<Keyframe>) => void;
  deleteKeyframe: (trackId: string, keyframeIndex: number) => void;
  moveKeyframe: (trackId: string, keyframeIndex: number, newTime: number) => void;

  // Selection
  selectTrack: (trackId: string | null) => void;
  selectKeyframe: (trackId: string, keyframeIndex: number | null) => void;
  clearSelection: () => void;

  // Playback
  play: (layers?: import('@/types/layers').Layer[]) => void;
  pause: () => void;
  stop: () => void;
  seek: (time: number) => void;
  setPlaybackRate: (rate: number) => void;

  // UI
  setTimelineZoom: (zoom: number) => void;
  setTimelineScrollOffset: (offset: number) => void;
  setShowTimeline: (show: boolean) => void;
  toggleTimeline: () => void;

  // Engine
  initEngine: () => void;
  disposeEngine: () => void;

  // Utility
  getTrackById: (trackId: string) => AnimationTrack | undefined;
  getTracksForLayer: (layerId: string) => AnimationTrack[];
  hasAnimations: () => boolean;
}

// ============================================================================
// STORE
// ============================================================================

const initialState: AnimationState = {
  timeline: null,
  playbackState: 'idle',
  currentTime: 0,
  duration: 0,
  selectedTrackId: null,
  selectedKeyframeIndex: null,
  timelineZoom: 100, // 100 pixels per second
  timelineScrollOffset: 0,
  showTimeline: false,
  _engine: null,
};

export const useAnimationStore = create<AnimationState & AnimationActions>()(
  immer((set, get) => ({
    ...initialState,

    // ========================================================================
    // Timeline Management
    // ========================================================================

    initTimeline: () => {
      set((state) => {
        if (!state.timeline) {
          state.timeline = createTimeline();
        }
      });
    },

    setTimeline: (timeline) => {
      // Dispose existing engine first
      const { _engine } = get();
      if (_engine) {
        _engine.dispose();
      }

      set((state) => {
        state.timeline = timeline;
        state.duration = timeline.duration ?? calculateTimelineDuration(timeline.tracks);
        state.currentTime = 0;
        state.playbackState = 'idle';
        state._engine = null;
      });
    },

    clearTimeline: () => {
      const { _engine } = get();
      if (_engine) {
        _engine.dispose();
      }

      set((state) => {
        state.timeline = null;
        state.playbackState = 'idle';
        state.currentTime = 0;
        state.duration = 0;
        state.selectedTrackId = null;
        state.selectedKeyframeIndex = null;
        state._engine = null;
      });
    },

    updateTimelineSettings: (settings) => {
      set((state) => {
        if (!state.timeline) return;
        Object.assign(state.timeline, settings);
        if (settings.duration !== undefined) {
          state.duration = settings.duration;
        }
      });
    },

    // ========================================================================
    // Track Management
    // ========================================================================

    addTrack: (layerId, name) => {
      const track = createTrack(layerId, name ?? 'Animation', [
        createKeyframe(0, {}),
      ]);

      set((state) => {
        if (!state.timeline) {
          state.timeline = createTimeline();
        }
        state.timeline.tracks.push(track);
        state.duration = calculateTimelineDuration(state.timeline.tracks);
      });

      recordChange('Add Track');
      return track;
    },

    addTrackFromPreset: (layerId, preset, options) => {
      const track = createTrackFromPreset(preset, layerId, {
        startTime: options?.startTime ?? 0,
        duration: options?.duration,
      });

      set((state) => {
        if (!state.timeline) {
          state.timeline = createTimeline();
        }
        state.timeline.tracks.push(track);
        state.duration = calculateTimelineDuration(state.timeline.tracks);
      });

      recordChange('Add Animation');
      return track;
    },

    updateTrack: (trackId, updates) => {
      set((state) => {
        if (!state.timeline) return;
        const track = state.timeline.tracks.find(t => t.id === trackId);
        if (track) {
          Object.assign(track, updates);
          state.duration = calculateTimelineDuration(state.timeline.tracks);
        }
      });
      recordChange('Update Track');
    },

    deleteTrack: (trackId) => {
      set((state) => {
        if (!state.timeline) return;
        state.timeline.tracks = state.timeline.tracks.filter(t => t.id !== trackId);
        if (state.selectedTrackId === trackId) {
          state.selectedTrackId = null;
          state.selectedKeyframeIndex = null;
        }
        state.duration = calculateTimelineDuration(state.timeline.tracks);
      });
      recordChange('Delete Track');
    },

    duplicateTrack: (trackId) => {
      const { timeline } = get();
      if (!timeline) return null;

      const original = timeline.tracks.find(t => t.id === trackId);
      if (!original) return null;

      const duplicate: AnimationTrack = {
        ...original,
        id: `track-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        name: `${original.name} Copy`,
        keyframes: original.keyframes.map(kf => ({ ...kf })),
      };

      set((state) => {
        state.timeline!.tracks.push(duplicate);
      });

      recordChange('Duplicate Track');
      return duplicate;
    },

    setTrackEnabled: (trackId, enabled) => {
      set((state) => {
        if (!state.timeline) return;
        const track = state.timeline.tracks.find(t => t.id === trackId);
        if (track) {
          track.enabled = enabled;
        }
      });
      recordChange(enabled ? 'Enable Track' : 'Disable Track');
    },

    // ========================================================================
    // Keyframe Management
    // ========================================================================

    addKeyframe: (trackId, time, properties) => {
      set((state) => {
        if (!state.timeline) return;
        const track = state.timeline.tracks.find(t => t.id === trackId);
        if (!track) return;

        // Check if keyframe already exists at this time
        const existingIndex = track.keyframes.findIndex(kf => kf.time === time);
        if (existingIndex >= 0) {
          // Merge properties
          Object.assign(track.keyframes[existingIndex].properties, properties);
        } else {
          // Add new keyframe
          track.keyframes.push(createKeyframe(time, properties));
          // Sort by time
          track.keyframes.sort((a, b) => a.time - b.time);
        }

        state.duration = calculateTimelineDuration(state.timeline.tracks);
      });
      recordChange('Add Keyframe');
    },

    updateKeyframe: (trackId, keyframeIndex, updates) => {
      set((state) => {
        if (!state.timeline) return;
        const track = state.timeline.tracks.find(t => t.id === trackId);
        if (!track || !track.keyframes[keyframeIndex]) return;

        Object.assign(track.keyframes[keyframeIndex], updates);

        // Re-sort if time changed
        if (updates.time !== undefined) {
          track.keyframes.sort((a, b) => a.time - b.time);
        }

        state.duration = calculateTimelineDuration(state.timeline.tracks);
      });
      recordChange('Update Keyframe');
    },

    deleteKeyframe: (trackId, keyframeIndex) => {
      set((state) => {
        if (!state.timeline) return;
        const track = state.timeline.tracks.find(t => t.id === trackId);
        if (!track) return;

        track.keyframes.splice(keyframeIndex, 1);

        if (state.selectedTrackId === trackId && state.selectedKeyframeIndex === keyframeIndex) {
          state.selectedKeyframeIndex = null;
        }

        state.duration = calculateTimelineDuration(state.timeline.tracks);
      });
      recordChange('Delete Keyframe');
    },

    moveKeyframe: (trackId, keyframeIndex, newTime) => {
      set((state) => {
        if (!state.timeline) return;
        const track = state.timeline.tracks.find(t => t.id === trackId);
        if (!track || !track.keyframes[keyframeIndex]) return;

        track.keyframes[keyframeIndex].time = Math.max(0, newTime);
        track.keyframes.sort((a, b) => a.time - b.time);

        state.duration = calculateTimelineDuration(state.timeline.tracks);
      });
      recordChange('Move Keyframe');
    },

    // ========================================================================
    // Selection
    // ========================================================================

    selectTrack: (trackId) => {
      set((state) => {
        state.selectedTrackId = trackId;
        state.selectedKeyframeIndex = null;
      });
    },

    selectKeyframe: (trackId, keyframeIndex) => {
      set((state) => {
        state.selectedTrackId = trackId;
        state.selectedKeyframeIndex = keyframeIndex;
      });
    },

    clearSelection: () => {
      set((state) => {
        state.selectedTrackId = null;
        state.selectedKeyframeIndex = null;
      });
    },

    // ========================================================================
    // Playback
    // ========================================================================

    play: (layers?: import('@/types/layers').Layer[]) => {
      const { _engine, timeline } = get();
      if (!timeline) return;

      let engine = _engine;
      if (!engine) {
        engine = new AnimationEngine(timeline);
        engine.initialize();

        // Initialize audio if layers provided
        if (layers) {
          engine.initializeAudio(layers);
        }

        // Subscribe to state changes
        engine.onStateChange((state) => {
          set({ playbackState: state });
        });
        engine.onTimeUpdate((time) => {
          set({ currentTime: time });
        });

        set({ _engine: engine });
      } else if (layers) {
        // Re-initialize audio if layers changed
        engine.initializeAudio(layers);
      }

      engine.play();
    },

    pause: () => {
      const { _engine } = get();
      _engine?.pause();
    },

    stop: () => {
      const { _engine } = get();
      _engine?.stop();
      set({ currentTime: 0, playbackState: 'idle' });
    },

    seek: (time) => {
      const { _engine, duration } = get();
      const clampedTime = Math.max(0, Math.min(time, duration));
      set({ currentTime: clampedTime });
      _engine?.seek(clampedTime);
    },

    setPlaybackRate: (rate) => {
      const { _engine, timeline } = get();
      if (timeline) {
        set((state) => {
          if (state.timeline) {
            state.timeline.playbackRate = rate;
          }
        });
      }
      _engine?.setPlaybackRate(rate);
    },

    // ========================================================================
    // UI
    // ========================================================================

    setTimelineZoom: (zoom) => {
      set({ timelineZoom: Math.max(10, Math.min(500, zoom)) });
    },

    setTimelineScrollOffset: (offset) => {
      set({ timelineScrollOffset: Math.max(0, offset) });
    },

    setShowTimeline: (show) => {
      set({ showTimeline: show });
    },

    toggleTimeline: () => {
      set((state) => {
        state.showTimeline = !state.showTimeline;
      });
    },

    // ========================================================================
    // Engine
    // ========================================================================

    initEngine: () => {
      const { timeline, _engine } = get();
      if (!timeline || _engine) return;

      const engine = new AnimationEngine(timeline);
      engine.initialize();

      engine.onStateChange((state) => {
        set({ playbackState: state });
      });
      engine.onTimeUpdate((time) => {
        set({ currentTime: time });
      });

      set({ _engine: engine });
    },

    disposeEngine: () => {
      const { _engine } = get();
      if (_engine) {
        _engine.dispose();
        set({ _engine: null });
      }
    },

    // ========================================================================
    // Utility
    // ========================================================================

    getTrackById: (trackId) => {
      const { timeline } = get();
      return timeline?.tracks.find(t => t.id === trackId);
    },

    getTracksForLayer: (layerId) => {
      const { timeline } = get();
      return timeline?.tracks.filter(t => t.layerId === layerId) ?? [];
    },

    hasAnimations: () => {
      const { timeline } = get();
      return (timeline?.tracks.length ?? 0) > 0;
    },
  }))
);

// Selector hooks
export const useTimeline = () => useAnimationStore((s) => s.timeline);
export const usePlaybackState = () => useAnimationStore((s) => s.playbackState);
export const useCurrentTime = () => useAnimationStore((s) => s.currentTime);
export const useShowTimeline = () => useAnimationStore((s) => s.showTimeline);
export const useSelectedTrack = () => {
  const trackId = useAnimationStore((s) => s.selectedTrackId);
  const timeline = useAnimationStore((s) => s.timeline);
  return trackId ? timeline?.tracks.find(t => t.id === trackId) : null;
};
